import { defineStore } from 'pinia';
import { useUiStore } from './ui';
import { useBrandStore } from './brand';
import { CurrentCustomer, Customer } from '@/types/customer';
import { CustomerPermission } from '~/plugins/services/auth/types';

const INITIAL_CUSTOMER_STATE = {
  currentCustomerId: '',
  currentProjectId: '',
  currentCustomerName: '',
  currentProjectName: '',
  currentIsSuspendedFlag: undefined,
  customers: (): Customer[] => [],
};

export const useCustomerStore = defineStore('customer', () => {
  const currentCustomerId = ref(INITIAL_CUSTOMER_STATE.currentCustomerId);
  const currentProjectId = ref<string | null | undefined>(INITIAL_CUSTOMER_STATE.currentProjectId);
  const currentCustomerName = ref<string | undefined>(INITIAL_CUSTOMER_STATE.currentCustomerName);
  const currentProjectName = ref<string | undefined>(INITIAL_CUSTOMER_STATE.currentProjectName);
  const currentIsSuspendedFlag = ref<boolean | undefined>(INITIAL_CUSTOMER_STATE.currentIsSuspendedFlag);
  const customers = ref(INITIAL_CUSTOMER_STATE.customers());

  const setCurrentCustomer = (payload: CustomerPermission) => {
    currentCustomerId.value = payload.customerId;
    currentCustomerName.value = payload.customerName;
    currentProjectId.value = payload.projectId;
    currentProjectName.value = payload.projectName;
    currentIsSuspendedFlag.value = payload.isSuspended;
  };

  const fetchCustomers = async () => {
    const { $authService } = useNuxtApp();
    const { isPunchout } = useUiStore();
    const { brand } = useBrandStore();
    const additionalOptions = ['dahl', 'opti'].includes(brand) ? { verifyShowAllCustomers: true } : {};

    const { data } = await $authService.getCustomers({
      params: { active: 1, virtualContext: isPunchout ? undefined : false, ...additionalOptions },
    });
    if (!data) return;
    customers.value = data;
  };

  const clear = () => {
    currentCustomerId.value = INITIAL_CUSTOMER_STATE.currentCustomerId;
    currentProjectId.value = INITIAL_CUSTOMER_STATE.currentProjectId;
    currentCustomerName.value = INITIAL_CUSTOMER_STATE.currentCustomerName;
    currentProjectName.value = INITIAL_CUSTOMER_STATE.currentProjectName;
    currentIsSuspendedFlag.value = INITIAL_CUSTOMER_STATE.currentIsSuspendedFlag;
    customers.value = INITIAL_CUSTOMER_STATE.customers();
    localStorage.removeItem('shop/customer/currentCustomer');
  };

  const getCurrentCustomer = computed<CurrentCustomer>(() => {
    return {
      projectId: currentProjectId.value,
      projectName: currentProjectName.value,
      customerId: currentCustomerId.value,
      customerName: currentCustomerName.value,
      isSuspended: currentIsSuspendedFlag.value,
    };
  });

  const getContextDisplayName = computed<string>(() => {
    const projectName = currentProjectName.value || currentProjectId.value;
    const customerName = currentCustomerName.value || currentCustomerId.value;
    if (!customerName && projectName) return projectName;
    if (projectName) return `${customerName} / ${projectName}`;
    return customerName;
  });

  return {
    customers,
    currentCustomerName,
    currentCustomerId,
    currentProjectId,
    currentProjectName,
    fetchCustomers,
    clear,
    setCurrentCustomer,
    getCurrentCustomer,
    getContextDisplayName,
  };
});
